import { type FC } from "react";
import { Button, Stack } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Filter, FilterProvider } from "@/partner/shared/components/Filter";
import { ContactListsList } from "./ContactListsList";
import { ContactListNameDialog } from "./ContactListNameDialog";
import { ContactListDialog } from "./ContactListDialog";

/**
 * Контакты (спека marketing-automations, фаза 2): списки адресов для
 * рассылок, устроены как рассылки и библиотека писем. Поиск внутри панели
 * фильтра, создание коротким диалогом по ?tab=create, список с контактами
 * и импортом fullScreen-диалогом по ?tab=edit&id=.
 */
const MarketingContactsInner: FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab");
  const editId = searchParams.get("id");

  const openCreate = () => {
    searchParams.set("tab", "create");
    setSearchParams(searchParams);
  };

  const openEdit = (id: string) => {
    searchParams.set("tab", "edit");
    searchParams.set("id", id);
    setSearchParams(searchParams);
  };

  // Созданный список сразу открывается для импорта, адрес меняется без
  // новой записи в истории: «Назад» не должен снова открывать создание.
  const onCreated = (id: string) => {
    searchParams.set("tab", "edit");
    searchParams.set("id", id);
    setSearchParams(searchParams, { replace: true });
  };

  const closeDialog = () => {
    searchParams.delete("tab");
    searchParams.delete("id");
    setSearchParams(searchParams);
  };

  return (
    <>
      <Stack spacing={1}>
        <Filter
          title={t("marketingContacts.list.title")}
          action={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disableElevation
              onClick={openCreate}
            >
              {t("common.actions.add")}
            </Button>
          }
          fields={{ search: true }}
        />
        <ContactListsList onEdit={openEdit} />
      </Stack>
      {tab === "create" && (
        <ContactListNameDialog
          list={null}
          onClose={closeDialog}
          onCreated={onCreated}
        />
      )}
      {tab === "edit" && editId && (
        <ContactListDialog key={editId} id={editId} onClose={closeDialog} />
      )}
    </>
  );
};

export const MarketingContacts: FC = () => (
  <FilterProvider>
    <MarketingContactsInner />
  </FilterProvider>
);
